import React from 'react'
import { Button, csx } from '@vtex/admin-ui'
import { useIntl, FormattedMessage } from 'react-intl'

import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerTitle,
} from '../../../../../../ui/components'
import type { DrawerProps } from '../../../../../../ui/components'
import { SalesPeriodDrawerSubTitle } from './SalesPeriodDrawerSubTitle'
import {
  SalesPeriodPicker,
  useSalesPeriodPickerState,
} from './SalesPeriodPicker'
import { SalesPeriodText } from './SalesPeriodText'
import type { SalesPeriod } from './SalesPeriod'

export type SalesPeriodDrawerOptions = {
  isOpen: boolean
  onChange: (value: SalesPeriod) => void
  initialValue: SalesPeriod
}

export type SalesPeriodDrawerProps = Omit<
  DrawerProps,
  keyof SalesPeriodDrawerOptions
> &
  SalesPeriodDrawerOptions

export const SalesPeriodDrawer = ({
  isOpen,
  initialValue,
  onChange,
  ...props
}: SalesPeriodDrawerProps) => {
  const intl = useIntl()
  const state = useSalesPeriodPickerState(initialValue)

  return (
    <Drawer
      isOpen={isOpen}
      closeOnOverlayClick
      className={csx({ maxWidth: '100vw' })}
      {...props}
    >
      <DrawerCloseButton />
      <div
        className={csx({
          display: 'flex',
          flexDirection: 'column',
          gap: '$space-3',
        })}
      >
        <DrawerTitle
          className={csx({
            marginBottom: '$space-0',
          })}
        >
          <FormattedMessage id="sales-performance.sales-period.show" />
        </DrawerTitle>
        <SalesPeriodDrawerSubTitle className={csx({ display: 'block' })}>
          <FormattedMessage
            id="sales-performance.sales-period-drawer.compared-to-sales-period-of-store"
            values={{
              period: (
                <SalesPeriodText value={state.form.getValues()} month="long" />
              ),
            }}
          />
        </SalesPeriodDrawerSubTitle>

        <DrawerBody
          className={csx({
            display: 'flex',
            flexDirection: 'column',
            gap: '$space-6',
          })}
        >
          <SalesPeriodPicker state={state} />
          <Button
            aria-label={intl.formatMessage({ id: 'confirm' })}
            disabled={Object.keys(state.form.formState.errors).length > 0}
            onClick={() => {
              onChange(state.form.getValues())
              props.onClose()
            }}
            className={csx({ ':disabled': { cursor: 'not-allowed' } })}
          >
            <FormattedMessage id="confirm" />
          </Button>
        </DrawerBody>
      </div>
    </Drawer>
  )
}
